// Schema-aware table for `data-sources-v1` registry files (DSR
// 1.2.0). Extracted from pages/DataSourcesRegistry.tsx when the page
// grew one-tab-per-registry-file — this component is the pre-1.2.0
// page body: YAML parse, KPI cards, search + filter dropdowns,
// sortable table with expandable detail rows, and the add-entry
// modal that writes back through the documents API.
//
// Props are the already-fetched YAML text plus the write-back
// coordinates (registryPath for the uploaded filename, containerId
// for the container reassign). The parent page owns fetching.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import yaml from 'js-yaml';
import { validateRegistryEntry } from '@captain-tasko/shared';
import { apiSingle, getCsrfToken } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';
import { WarningRow } from './WarningRow.js';
import { RegistryTableHeader } from './RegistryTableHeader.js';
import { InstalledByBadge } from './InstalledByBadge.js';
import { DataSourceRowEditor } from './DataSourceRowEditor.js';
import { ChevronUp, ChevronDown, Copy, Pencil, X, ExternalLink, Mail, Link as LinkIcon } from 'lucide-react';

// ── Schema, mirrors data_sources.yaml ───────────────────────────────
//
// CTSK-303 — entries are kind-discriminated: `external-url` (the
// original shape; `kind` absent on pre-CTSK-303 entries) or
// `internal-registry` (a CT tool's own registry file, self-registered
// at manifest install / bundle import / backfill). Internal entries
// carry the target_* block instead of the url/frequency/cost fields.
type DataSourceKind = 'external-url' | 'internal-registry';

interface DataSource {
  kind?: DataSourceKind;
  id: string;
  name: string;
  type: SourceType;
  status: SourceStatus;
  description: string;
  url: string;
  owner: { name: string; email: string };
  update_frequency: UpdateFrequency;
  subscription: Subscription;
  license: string;
  cost_annual_usd?: number | null;
  tools_used?: string[];
  tags?: string[];
  last_reviewed: string;
  notes?: string;
  // internal-registry fields (CTSK-303)
  target_tool?: string;
  target_file?: string;
  endpoint?: string;
  direction?: 'inbound' | 'bidirectional';
  known_producers?: string[];
  installed_by?: string;
  installed_at?: string;
  // CTSK-366 — canonical pointer to the schema this internal-registry
  // entry's target file conforms to. Optional; absent on every entry
  // until the operator populates it via the CTSK-364 whole-doc editor.
  schemaRef?: {
    documentId?: string;
    url?: string;
    label?: string;
  };
}

/** Effective kind — absent `kind` means a pre-CTSK-303 external entry. */
function kindOf(s: DataSource): DataSourceKind {
  return s.kind === 'internal-registry' ? 'internal-registry' : 'external-url';
}

/** CTSK-360 — chip DISPLAY label only. The underlying kind value (data
 *  model, filter options, YAML) stays `internal-registry`; the chip
 *  text is shortened for visual scan. External chips are untouched. */
function kindChipLabel(k: DataSourceKind): string {
  return k === 'internal-registry' ? 'internal' : 'external-url';
}

type SourceType =
  | 'api' | 'database' | 'file_feed' | 'scraper' | 'dataset'
  | 'stream' | 'sdk' | 'manual' | 'other';
type SourceStatus = 'active' | 'evaluating' | 'deprecated' | 'archived';
type UpdateFrequency =
  | 'realtime' | 'hourly' | 'daily' | 'weekly' | 'monthly'
  | 'quarterly' | 'annual' | 'ad_hoc' | 'unknown';
type Subscription =
  | 'free' | 'freemium' | 'paid' | 'trial' | 'internal' | 'open_source';

const SOURCE_TYPES: SourceType[] = ['api', 'database', 'file_feed', 'scraper', 'dataset', 'stream', 'sdk', 'manual', 'other'];
const SOURCE_STATUSES: SourceStatus[] = ['active', 'evaluating', 'deprecated', 'archived'];
const UPDATE_FREQUENCIES: UpdateFrequency[] = ['realtime', 'hourly', 'daily', 'weekly', 'monthly', 'quarterly', 'annual', 'ad_hoc', 'unknown'];
const SUBSCRIPTIONS: Subscription[] = ['free', 'freemium', 'paid', 'trial', 'internal', 'open_source'];

interface RegistryFile {
  schema_version?: number;
  sources?: DataSource[];
  // CTSK-303 — the shared topLevelKeyForSchema('data-sources-v1') key
  // is 'data_sources'; the live Vault file predates that helper and
  // uses 'sources'. Read BOTH (merged) so entries stay visible under
  // either convention; the add-entry write-back keeps whichever key
  // the file already used.
  data_sources?: DataSource[];
  [k: string]: unknown;
}

// ── Component ─────────────────────────────────────────────────────────

/** CTSK-367 — write-path wiring (mirrors BookmarksTable's
 *  BookmarksMutationContext). When present the table gains a per-row
 *  Edit chip + inline DataSourceRowEditor (one open at a time) over
 *  the CTSK-364 PATCH endpoint. Absent → read-only, byte-for-byte. */
export interface DataSourcesMutationContext {
  toolId: string;
  filePath: string;
  /** Refetch the registry YAML — the table re-renders from persisted
   *  bytes, no optimistic state. */
  onRefetch: () => void;
}

export function DataSourcesTable({
  yamlText,
  registryPath,
  containerId,
  mutation,
}: {
  yamlText: string;
  registryPath: string;
  containerId?: string;
  /** CTSK-367 — write-path context; absent = read-only table. */
  mutation?: DataSourcesMutationContext;
}) {
  const qc = useQueryClient();
  const editable = Boolean(mutation);
  // CTSK-367 — at most one row's editor open (opening another closes
  // the first; toggling the same row closes it).
  const [editingId, setEditingId] = useState<string | null>(null);
  const columnCount = 8 + (editable ? 1 : 0);

  // Parse YAML → entries. Memoize so re-renders don't re-parse.
  //    js-yaml resolves `last_reviewed: 2026-05-23` (and similar
  //    ISO-shaped scalars) to JS `Date` objects per the YAML 1.1
  //    timestamp tag. Rendering a Date into JSX throws "Objects are
  //    not valid as a React child". Normalize every entry so date-
  //    typed fields come back as YYYY-MM-DD strings.
  const { entries, parseError, fileShape } = useMemo<{
    entries: DataSource[];
    parseError: string | null;
    fileShape: RegistryFile | null;
  }>(() => {
    if (!yamlText) return { entries: [], parseError: null, fileShape: null };
    try {
      const parsed = yaml.load(yamlText) as RegistryFile | null;
      // CTSK-303 — merge both known list keys (see RegistryFile note).
      const list = ([
        ...((parsed?.sources ?? []) as DataSource[]),
        ...((parsed?.data_sources ?? []) as DataSource[]),
      ]).map(normalizeEntry);
      return { entries: list, parseError: null, fileShape: parsed ?? null };
    } catch (err) {
      return { entries: [], parseError: (err as Error).message, fileShape: null };
    }
  }, [yamlText]);

  // ── Selected source (for the inline detail panel + ?id= deep-link)
  //
  // The URL mirrors the currently-expanded source via ?id=<source-id>
  // so an operator can copy the address bar (or hit "Copy link") to
  // share a direct pointer. Hydrated from window.location.search on
  // mount; updated via replaceState (no back-button pollution) when
  // the operator opens/closes a row.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('id');
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    if (selectedId) sp.set('id', selectedId); else sp.delete('id');
    const qs = sp.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', next);
  }, [selectedId]);
  // Scroll the expanded row into view when the URL has an ?id= on
  // first paint, OR when the operator clicks a row that's currently
  // out of viewport. Cheap — single querySelector lookup, no observers.
  useEffect(() => {
    if (!selectedId) return;
    const el = document.querySelector(`[data-source-id="${CSS.escape(selectedId)}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedId]);

  // ── Filtering / sorting state ──────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterKind, setFilterKind] = useState<DataSourceKind | ''>('');
  // CTSK-310 — per-tool filter: collapses the N internal-registry rows
  // of one tool (e.g. Timeline Lane's four files) to a single-tool
  // view. Options are the distinct target_tool values present, so the
  // dropdown only renders once internal rows exist.
  const [filterTool, setFilterTool] = useState('');
  const [filterType, setFilterType] = useState<SourceType | ''>('');
  const [filterStatus, setFilterStatus] = useState<SourceStatus | ''>('');
  const [filterSub, setFilterSub] = useState<Subscription | ''>('');
  const [filterFreq, setFilterFreq] = useState<UpdateFrequency | ''>('');
  const [sortKey, setSortKey] = useState<keyof DataSource | 'cost'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filtered = useMemo(() => {
    let rows = entries;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => {
        const hay = [
          r.name, r.description, r.id, r.url, r.owner?.email, r.owner?.name,
          ...(r.tags ?? []), ...(r.tools_used ?? []), r.notes ?? '',
          // CTSK-303 — internal-registry fields are searchable too.
          r.target_tool ?? '', r.target_file ?? '', r.endpoint ?? '',
          ...(r.known_producers ?? []),
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    if (filterKind) rows = rows.filter((r) => kindOf(r) === filterKind);
    if (filterTool) rows = rows.filter((r) => r.target_tool === filterTool);
    if (filterType) rows = rows.filter((r) => r.type === filterType);
    if (filterStatus) rows = rows.filter((r) => r.status === filterStatus);
    if (filterSub) rows = rows.filter((r) => r.subscription === filterSub);
    if (filterFreq) rows = rows.filter((r) => r.update_frequency === filterFreq);

    rows = [...rows].sort((a, b) => {
      const k = sortKey === 'cost' ? 'cost_annual_usd' : sortKey;
      // Sort keys are typed-safe because we constrain to DataSource
      // keys in state, but the indexing needs an unknown bounce-cast
      // to satisfy the typechecker.
      const av = (a as unknown as Record<string, unknown>)[k];
      const bv = (b as unknown as Record<string, unknown>)[k];
      // null/undefined sort last
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      // Comparable types only — strings + numbers will work; other
      // types fall through to equal which is fine for the table.
      if (typeof av !== typeof bv) return 0;
      if ((av as number | string) < (bv as number | string)) return sortDir === 'asc' ? -1 : 1;
      if ((av as number | string) > (bv as number | string)) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [entries, search, filterKind, filterTool, filterType, filterStatus, filterSub, filterFreq, sortKey, sortDir]);

  // CTSK-310 — distinct target_tool values for the per-tool filter.
  const toolOptions = useMemo(
    () => Array.from(new Set(entries.map((e) => e.target_tool).filter((t): t is string => Boolean(t)))).sort(),
    [entries],
  );

  // KPI metrics
  const kpis = useMemo(() => {
    const total = entries.length;
    const byStatus = (s: SourceStatus) => entries.filter((e) => e.status === s).length;
    const totalCost = entries.reduce((sum, e) => sum + (e.cost_annual_usd ?? 0), 0);
    const paidCount = entries.filter((e) => e.subscription === 'paid' || e.subscription === 'freemium').length;
    const internal = entries.filter((e) => kindOf(e) === 'internal-registry').length;
    return {
      total,
      active: byStatus('active'),
      evaluating: byStatus('evaluating'),
      deprecated: byStatus('deprecated'),
      archived: byStatus('archived'),
      totalCost,
      paidCount,
      internal,
    };
  }, [entries]);

  // ── Add-entry form ─────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false);
  const [draft, setDraft] = useState<Partial<DataSource>>(blankDraft());

  const addMut = useMutation({
    mutationFn: async () => {
      const newEntry = normalizeDraft(draft);
      // Validate uniqueness
      if (entries.some((e) => e.id === newEntry.id)) {
        throw new Error(`Entry with id "${newEntry.id}" already exists`);
      }
      // CTSK-303 — write back under the key the file already uses
      // ('data_sources' per topLevelKeyForSchema, or the live file's
      // legacy 'sources'), merging if it somehow carried both so no
      // entry is duplicated across two keys.
      const usesDataSources = Array.isArray(fileShape?.data_sources) && !Array.isArray(fileShape?.sources);
      const listKey = usesDataSources ? 'data_sources' : 'sources';
      const updated: RegistryFile = {
        ...(fileShape ?? {}),
        schema_version: fileShape?.schema_version ?? 1,
        [listKey]: [...entries, newEntry],
      };
      if (listKey === 'sources') delete updated.data_sources;
      else delete updated.sources;
      const yamlOut = yaml.dump(updated, { lineWidth: 100, noRefs: true });

      // Upload the new yaml content. Use multipart to replace the
      // existing document's content via re-upload + relink. Simpler
      // than building a separate PUT-content path: we POST a new
      // document with the same filename + reassign to the container,
      // then delete the old one.
      //
      // BUT — simpler still: the existing PUT /documents/:id/content
      // doesn't exist either. So we use the documents/upload route
      // with a multipart body and the same originalFilename, then
      // soft-delete the old.
      //
      // Even simpler for the first cut: write a new file with a
      // timestamp suffix, reassign the registryPath to it, and let
      // the operator clean up the older version manually. This
      // preserves history in the documents list which is a small
      // accident-of-implementation that's actually nice for an
      // audit trail.
      const blob = new Blob([yamlOut], { type: 'text/yaml' });
      const form = new FormData();
      form.append('file', blob, registryPath);
      const csrf = getCsrfToken();
      const res = await fetch('/api/v1/documents/upload', {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
        body: form,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Upload failed (${res.status}): ${text}`);
      }
      const json = (await res.json()) as { data: { document: { id: string } } };
      const newDocId = json.data.document.id;

      // Reassign the new doc to the tool's container
      if (containerId) {
        await apiSingle<{ ok: boolean }>(`/documents/${newDocId}/container`, {
          method: 'PUT',
          body: { containerId },
        });
      }
      return newDocId;
    },
    onSuccess: () => {
      toast.success('Entry added — registry updated');
      setShowAddForm(false);
      setDraft(blankDraft());
      qc.invalidateQueries({ queryKey: ['tool-registry'] });
      qc.invalidateQueries({ queryKey: ['tool-registry-content'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to add entry'),
  });

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div>
      {/* Parse error banner */}
      {parseError && (
        <div role="alert" style={{ padding: 12, background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 12%, var(--surface-raised))', border: '1px solid var(--danger-primary, #B3261E)', borderRadius: 'var(--radius-md)', color: 'var(--danger-primary, #B3261E)', fontSize: 13, marginBottom: 16 }}>
          <strong>YAML parse error:</strong> {parseError}
        </div>
      )}

      {/* KPIs */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Total sources" value={String(kpis.total)} />
        <Kpi label="Active" value={String(kpis.active)} sub={`${pct(kpis.active, kpis.total)}%`} color="var(--success-primary)" />
        <Kpi label="Evaluating" value={String(kpis.evaluating)} color="var(--warning-primary)" />
        <Kpi label="Deprecated" value={String(kpis.deprecated)} color="var(--danger-primary, #B3261E)" />
        <Kpi label="Annual cost (USD)" value={fmtMoney(kpis.totalCost)} sub={`${kpis.paidCount} paid`} />
        {/* CTSK-303 — internal registry files auto-listed via DSR
            self-registration. Hidden while zero (pre-backfill orgs). */}
        {kpis.internal > 0 && (
          <Kpi label="Internal registries" value={String(kpis.internal)} color="var(--info-primary)" />
        )}
      </section>

      {/* Toolbar — CTSK-267: the shared registry-table header (also
          used by the four Timeline Lane table tabs). Reset keeps the
          pre-extraction behavior: search + filters clear, the sort
          column stays. */}
      <RegistryTableHeader
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, description, tags, tools, owner…"
        filters={[
          // CTSK-303 — kind filter (internal-registry vs external-url).
          { label: 'All kinds', value: filterKind, options: ['external-url', 'internal-registry'], onChange: (v) => setFilterKind(v as DataSourceKind | '') },
          // CTSK-310 — per-tool filter; only renders once internal
          // rows exist (external entries carry no target_tool).
          ...(toolOptions.length > 0
            ? [{ label: 'All tools', value: filterTool, options: toolOptions, onChange: (v: string) => setFilterTool(v) }]
            : []),
          { label: 'All types', value: filterType, options: SOURCE_TYPES, onChange: (v) => setFilterType(v as SourceType | '') },
          { label: 'All statuses', value: filterStatus, options: SOURCE_STATUSES, onChange: (v) => setFilterStatus(v as SourceStatus | '') },
          { label: 'All subscriptions', value: filterSub, options: SUBSCRIPTIONS, onChange: (v) => setFilterSub(v as Subscription | '') },
          { label: 'All frequencies', value: filterFreq, options: UPDATE_FREQUENCIES, onChange: (v) => setFilterFreq(v as UpdateFrequency | '') },
        ]}
        addEntryLabel="Add entry"
        onAddEntry={() => { setShowAddForm(true); setDraft(blankDraft()); }}
        onReset={() => { setSearch(''); setFilterKind(''); setFilterTool(''); setFilterType(''); setFilterStatus(''); setFilterSub(''); setFilterFreq(''); }}
      />

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          {entries.length === 0 ? 'No entries yet. Click "Add entry" to register the first source.' : 'No entries match.'}
        </div>
      ) : (
        <table className="grid" style={{ tableLayout: 'fixed' }}>
          {/* CTSK-310 — Source widened 260 → 340 so the "Tool Name —
              target-file.yaml" internal names (4 Timeline rows) read
              distinctly at row-glance. The four middle columns are
              dual-purpose (kind-aware): external rows keep their
              CTSK-303 content; internal rows swap in provenance /
              installed / target / direction. Headers carry both
              labels since the table mixes kinds. */}
          <colgroup>
            <col style={{ width: 340 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 110 }} />
            <col />
            {/* CTSK-367 — Actions col only when the table is mutable
                (no dead column artifact — matches BookmarksTable). */}
            {editable && <col style={{ width: 70 }} />}
          </colgroup>
          <thead>
            <tr>
              <SortableHeader k="name" label="Source" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="type" label="Type" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="status" label="Status" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="update_frequency" label="Update / Provenance" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="subscription" label="Subscription / Installed" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="license" label="License / Target" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="cost" label="Cost / Direction" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              <SortableHeader k="last_reviewed" label="Reviewed / Installed" sortKey={sortKey} sortDir={sortDir} onSort={(k) => sortClick(k, sortKey, sortDir, setSortKey, setSortDir)} />
              {editable && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, idx) => {
              const isOpen = selectedId === s.id;
              // CTSK-264 — read-time validation: same dispatcher as the
              // CTSK-255 write path, on the normalized entry. Invalid
              // rows keep their cells AND the click-to-expand behavior;
              // only the row treatment + tooltip change.
              const validation = validateRegistryEntry('data-sources-v1', s);
              const rowProps = {
                'data-source-id': s.id,
                onClick: () => setSelectedId(isOpen ? null : s.id),
                style: {
                  cursor: 'pointer' as const,
                  background: isOpen ? 'var(--surface-sunken)' : undefined,
                },
              };
              // CTSK-303 → CTSK-310 — internal-registry rows reuse the
              // fixed 8-column grid with KIND-AWARE middle cells: the
              // frequency/subscription/license/cost slots (external
              // concepts that rendered as em-dashes pre-CTSK-310) now
              // carry installed_by badge / installed_at date /
              // target_file / direction. The Type column shows the
              // entry's kind for internal rows (pre-CTSK-310 it
              // misrendered `direction` there), so the Source cell
              // drops its kind chip on internal rows — the freed width
              // is what lets the four "Timeline Lane — *.yaml" names
              // read distinctly. External rows are byte-identical to
              // their CTSK-303 rendering (backward-compat AC).
              const isInternal = kindOf(s) === 'internal-registry';
              const cells = (
                <>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${s.name}${s.description ? ` · ${s.description}` : ''}`}>
                      <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                        {!isInternal && <Chip kind="kind-external">external-url</Chip>}
                      </div>
                      {isInternal ? (
                        <>
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.target_tool} / {s.target_file}
                          </div>
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.endpoint}>
                            {s.endpoint}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.description}</div>
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.id}</div>
                        </>
                      )}
                    </td>
                    {/* Type — kind-aware (CTSK-310): internal rows show
                        the kind itself, not `direction` (which reads as
                        a bogus source type next to api/database rows).
                        Direction moved to the Cost/Direction slot. */}
                    <td>{isInternal
                      ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Chip kind="kind-internal">{kindChipLabel('internal-registry')}</Chip>
                          <SchemaChip schemaRef={s.schemaRef} />
                        </span>
                      )
                      : <Chip kind="type">{s.type}</Chip>}</td>
                    <td>{s.status
                      ? <Chip kind={`status-${s.status}` as ChipKind}>{s.status}</Chip>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    {/* Update / Provenance */}
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{isInternal ? <InstalledByBadge stamp={s.installed_by} /> : s.update_frequency}</td>
                    {/* Subscription / Installed */}
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{isInternal ? (s.installed_at ?? '').slice(0, 10) : s.subscription}</td>
                    {/* License / Target */}
                    <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(isInternal ? { fontFamily: 'var(--font-mono)' } : {}) }} title={isInternal ? `${s.target_tool ?? ''} / ${s.target_file ?? ''}` : s.license}>{isInternal ? s.target_file : s.license}</td>
                    {/* Cost / Direction */}
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap', ...(isInternal ? {} : { fontFamily: 'var(--font-mono)' }) }}>{isInternal
                      ? <Chip kind="type">{s.direction ?? 'inbound'}</Chip>
                      : (s.cost_annual_usd == null ? '—' : fmtMoney(s.cost_annual_usd))}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{isInternal ? (s.installed_at ?? '').slice(0, 10) : s.last_reviewed}</td>
                    {/* CTSK-367 — per-row Edit chip. stopPropagation:
                        the row's own onClick expands the detail panel;
                        Edit must not also toggle it. */}
                    {editable && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId((cur) => (cur === s.id ? null : s.id));
                          }}
                          title={`Edit ${s.id} inline`}
                          aria-expanded={editingId === s.id}
                          style={editActionBtnStyle}
                        >
                          <Pencil size={12} aria-hidden="true" />
                          <span>Edit</span>
                        </button>
                      </td>
                    )}
                </>
              );
              return (
                <Fragment key={s.id ?? `row-${idx}`}>
                  {validation.ok ? (
                    <tr {...rowProps} title={isOpen ? 'Click to collapse' : 'Click to view full detail'}>
                      {cells}
                    </tr>
                  ) : (
                    <WarningRow {...rowProps} issues={validation.errors}>
                      {cells}
                    </WarningRow>
                  )}
                  {/* CTSK-367 — inline editor expansion row directly
                      under the row being edited. Save refetches the
                      YAML (persisted bytes are the source of truth);
                      cancel just collapses. */}
                  {editable && mutation && editingId === s.id && (
                    <tr key={`${s.id}-editing`}>
                      <td colSpan={columnCount} style={{ padding: '4px 0 10px' }}>
                        <DataSourceRowEditor
                          source={s}
                          toolId={mutation.toolId}
                          filePath={mutation.filePath}
                          onSaved={() => {
                            setEditingId(null);
                            mutation.onRefetch();
                          }}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {isOpen && (
                    <tr key={`${s.id}-detail`} className="source-detail-row">
                      <td
                        colSpan={columnCount}
                        style={{
                          padding: 0,
                          background: 'var(--surface-sunken)',
                          borderTop: '1px solid var(--border-subtle)',
                        }}
                      >
                        <SourceDetail
                          source={s}
                          onClose={() => setSelectedId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add-entry modal */}
      {showAddForm && (
        <AddEntryDialog
          draft={draft}
          onChange={setDraft}
          onCancel={() => { setShowAddForm(false); setDraft(blankDraft()); }}
          onSubmit={() => addMut.mutate()}
          submitting={addMut.isPending}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

// Expanded detail panel for a single source. Renders below the row
// when clicked. Mirrors the original registry.html `<details>` block
// — every field that doesn't fit in the table, plus actionable
// links (URL, mailto:, copy deep-link). Form follows the data: any
// optional field that's empty is just omitted to keep the panel
// dense.
function SourceDetail({ source: s, onClose }: { source: DataSource; onClose: () => void }) {
  const deepLink = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}?id=${encodeURIComponent(s.id)}`
    : `?id=${encodeURIComponent(s.id)}`;

  const copyLink = () => {
    // CTSK-282: copyToClipboard has an execCommand fallback for insecure
    // origins (raw navigator.clipboard fails silently on http:// Vault).
    void copyToClipboard(deepLink).then((ok) => {
      if (ok) toast.success(`Link to ${s.id} copied`);
      else toast.error('Clipboard write failed — select the URL manually');
    });
  };

  return (
    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header row: name + actions */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            <code style={{ fontFamily: 'var(--font-mono)' }}>{s.id}</code>
            {' · '}<Chip kind={kindOf(s) === 'internal-registry' ? 'kind-internal' : 'kind-external'}>{kindChipLabel(kindOf(s))}</Chip>
            {s.type && <>{' '}<Chip kind="type">{s.type}</Chip></>}
            {s.status && <>{' '}<Chip kind={`status-${s.status}` as ChipKind}>{s.status}</Chip></>}
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyLink(); }}
            style={detailBtnStyle}
            // CTSK-360 — the button copies a shareable page deep-link
            // (?id=<entry>), NOT a tool/client endpoint. Tooltip says so.
            title={`Copy direct link to this entry: ${deepLink}`}
          >
            <Copy size={12} /> Copy link
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{ ...detailBtnStyle, color: 'var(--text-muted)' }}
            title="Collapse"
            aria-label="Collapse"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Description — full text, not truncated */}
      {s.description && (
        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {s.description}
        </div>
      )}

      {/* Field grid — CTSK-303: internal-registry entries show the
          target block instead of the external subscription fields. */}
      {kindOf(s) === 'internal-registry' ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) 1fr', gap: '6px 16px', fontSize: 12 }}>
        <Term>Target tool</Term>
        <Def style={{ fontFamily: 'var(--font-mono)' }}>{s.target_tool}</Def>

        <Term>Target file</Term>
        <Def style={{ fontFamily: 'var(--font-mono)' }}>{s.target_file}</Def>

        <Term>Endpoint</Term>
        <Def style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{s.endpoint}</Def>

        <Term>Direction</Term>
        <Def>{s.direction ?? 'inbound'}</Def>

        <Term>Known producers</Term>
        <Def>
          {(s.known_producers ?? []).length === 0 ? '—' : (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {(s.known_producers ?? []).map((p) => (
                <span
                  key={p}
                  style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-mono)' }}
                >
                  {p}
                </span>
              ))}
            </span>
          )}
        </Def>

        <Term>Installed by</Term>
        <Def style={{ fontFamily: 'var(--font-mono)' }}>{s.installed_by ?? '—'}</Def>

        <Term>Installed at</Term>
        <Def>{s.installed_at ?? '—'}</Def>

        {s.tags && s.tags.length > 0 && (
          <>
            <Term>Tags</Term>
            <Def>
              <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                {s.tags.map((t) => (
                  <span
                    key={t}
                    style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))', color: 'var(--accent-primary)' }}
                  >
                    {t}
                  </span>
                ))}
              </span>
            </Def>
          </>
        )}

        {s.notes && (
          <>
            <Term>Notes</Term>
            <Def>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{s.notes}</div>
            </Def>
          </>
        )}
      </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) 1fr', gap: '6px 16px', fontSize: 12 }}>
        <Term>URL</Term>
        <Def>
          <a
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'var(--accent-primary)', display: 'inline-flex', alignItems: 'center', gap: 4, wordBreak: 'break-all' }}
          >
            {s.url} <ExternalLink size={11} aria-hidden="true" />
          </a>
        </Def>

        <Term>Owner</Term>
        <Def>
          {s.owner?.name ?? '—'}
          {s.owner?.email && (
            <>
              {' '}·{' '}
              <a
                href={`mailto:${s.owner.email}`}
                onClick={(e) => e.stopPropagation()}
                style={{ color: 'var(--accent-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Mail size={11} aria-hidden="true" /> {s.owner.email}
              </a>
            </>
          )}
        </Def>

        <Term>Update frequency</Term>
        <Def>{s.update_frequency}</Def>

        <Term>Subscription</Term>
        <Def>{s.subscription}</Def>

        <Term>License</Term>
        <Def style={{ fontFamily: 'var(--font-mono)' }}>{s.license}</Def>

        <Term>Annual cost</Term>
        <Def style={{ fontFamily: 'var(--font-mono)' }}>
          {s.cost_annual_usd == null ? '—' : fmtMoney(s.cost_annual_usd)}
        </Def>

        <Term>Last reviewed</Term>
        <Def>{s.last_reviewed}</Def>

        {s.tools_used && s.tools_used.length > 0 && (
          <>
            <Term>Tools used</Term>
            <Def>
              <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                {s.tools_used.map((t) => (
                  <span
                    key={t}
                    style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-mono)' }}
                  >
                    {t}
                  </span>
                ))}
              </span>
            </Def>
          </>
        )}

        {s.tags && s.tags.length > 0 && (
          <>
            <Term>Tags</Term>
            <Def>
              <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                {s.tags.map((t) => (
                  <span
                    key={t}
                    style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))', color: 'var(--accent-primary)' }}
                  >
                    {t}
                  </span>
                ))}
              </span>
            </Def>
          </>
        )}

        {s.notes && (
          <>
            <Term>Notes</Term>
            <Def>
              {/* Preserve newlines from the YAML literal block. */}
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{s.notes}</div>
            </Def>
          </>
        )}
      </div>
      )}

      {/* Footer with the deep link spelled out for copy-by-keyboard */}
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          fontFamily: 'var(--font-mono)',
          padding: '6px 8px',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 4,
          userSelect: 'all',
          wordBreak: 'break-all',
        }}
        title="Triple-click to select; or use the Copy link button above"
      >
        {deepLink}
      </div>
    </div>
  );
}

// Tiny dt / dd helpers so the field grid stays terse to write.
function Term({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'baseline', paddingTop: 2 }}>
      {children}
    </div>
  );
}
function Def({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ minWidth: 0, ...style }}>{children}</div>;
}

// CTSK-367 — Edit chip, visually matched to BookmarksTable's Edit
// affordance / RegistryRowActionsPalette buttons so the two tabs'
// edit surfaces read identically.
const editActionBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: 'var(--accent-primary)',
  background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-raised))',
  border: '1px solid color-mix(in srgb, var(--accent-primary) 35%, var(--border-subtle))',
  borderRadius: 'var(--radius-sm, 4px)',
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

const detailBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  fontSize: 12,
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-strong)',
  borderRadius: 4,
  color: 'var(--text-primary)',
  cursor: 'pointer',
};

function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, color: color ?? 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

type ChipKind =
  | 'type' | 'status-active' | 'status-evaluating' | 'status-deprecated' | 'status-archived'
  // CTSK-303 — kind chips: internal-registry blue, external-url gray.
  | 'kind-internal' | 'kind-external';

function Chip({ kind, children }: { kind: ChipKind; children: React.ReactNode }) {
  const styles: Record<ChipKind, React.CSSProperties> = {
    'type':              { background: 'color-mix(in srgb, var(--info-primary) 18%, var(--surface-raised))',     color: 'var(--info-primary)' },
    'status-active':     { background: 'color-mix(in srgb, var(--success-primary) 18%, var(--surface-raised))',  color: 'var(--success-primary)' },
    'status-evaluating': { background: 'color-mix(in srgb, var(--warning-primary) 18%, var(--surface-raised))',  color: 'var(--warning-primary)' },
    'status-deprecated': { background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 14%, var(--surface-raised))', color: 'var(--danger-primary, #B3261E)' },
    'status-archived':   { background: 'var(--surface-sunken)', color: 'var(--text-muted)' },
    'kind-internal':     { background: 'color-mix(in srgb, var(--info-primary) 18%, var(--surface-raised))',     color: 'var(--info-primary)' },
    'kind-external':     { background: 'var(--surface-sunken)', color: 'var(--text-muted)' },
  };
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, ...styles[kind] }}>
      {children}
    </span>
  );
}

// CTSK-366 — "Schema" link/chip for internal-registry rows whose entry
// declares a schemaRef. Click behavior: documentId wins over url when
// both are present (internal reference over external one); documentId
// navigates same-tab (plain <a href>, no SPA router wired into this
// table today — see brief), url opens in a new tab. Neither field set
// (or schemaRef entirely absent) → render nothing, no dead affordance.
// Visual family matches the kind chip (compact, subdued, link glyph —
// same icon concept as the "link" entry in RegistryRowActionsPalette's
// CTSK-351 icon allowlist).
function SchemaChip({ schemaRef }: { schemaRef?: DataSource['schemaRef'] }) {
  if (!schemaRef) return null;
  const { documentId, url, label } = schemaRef;
  if (!documentId && !url) return null;
  const text = label ?? 'Schema';
  const href = documentId ? `/documents/${documentId}` : (url as string);
  const external = !documentId;
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={(e) => e.stopPropagation()}
      title={`${text}${external ? ' (opens in new tab)' : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        textDecoration: 'none',
        color: 'var(--text-muted)',
        background: 'var(--surface-sunken)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <LinkIcon size={10} aria-hidden="true" />
      {text}
    </a>
  );
}

function SortableHeader({
  k, label, sortKey, sortDir, onSort,
}: { k: string; label: string; sortKey: string; sortDir: 'asc' | 'desc'; onSort: (k: string) => void }) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.4 }}>
        {active && sortDir === 'asc' ? <ChevronUp size={11} style={{ verticalAlign: 'middle' }} />
          : active && sortDir === 'desc' ? <ChevronDown size={11} style={{ verticalAlign: 'middle' }} />
          : <ChevronDown size={11} style={{ verticalAlign: 'middle' }} />}
      </span>
    </th>
  );
}

function sortClick(
  k: string,
  cur: string,
  curDir: 'asc' | 'desc',
  setSort: (k: keyof DataSource | 'cost') => void,
  setDir: (d: 'asc' | 'desc') => void,
) {
  if (k === cur) setDir(curDir === 'asc' ? 'desc' : 'asc');
  else { setSort(k as keyof DataSource | 'cost'); setDir('asc'); }
}

// ── Add-entry dialog ──────────────────────────────────────────────────

function AddEntryDialog({
  draft, onChange, onCancel, onSubmit, submitting,
}: {
  draft: Partial<DataSource>;
  onChange: (d: Partial<DataSource>) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const set = <K extends keyof DataSource>(k: K, v: DataSource[K] | undefined) =>
    onChange({ ...draft, [k]: v });

  return (
    <div
      role="dialog"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', zIndex: 100, overflowY: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 720, padding: '22px 24px' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 17 }}>Register a new data source</h2>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 16 }}>
          Saves to <code style={{ fontFamily: 'var(--font-mono)' }}>data_sources.yaml</code> in the tool's container.
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
            <Field label="ID (kebab-case, unique) *">
              <input
                value={draft.id ?? ''}
                onChange={(e) => set('id', e.target.value)}
                required
                pattern="[a-z0-9][a-z0-9-]*"
                placeholder="vendor-csv-feed"
                style={inputStyle}
              />
            </Field>
            <Field label="Name *">
              <input value={draft.name ?? ''} onChange={(e) => set('name', e.target.value)} required placeholder="Vendor CSV Feed" style={inputStyle} />
            </Field>
          </div>

          <Field label="Description *">
            <textarea value={draft.description ?? ''} onChange={(e) => set('description', e.target.value)} required placeholder="One- or two-sentence summary." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
            <Field label="Type *"><select value={draft.type ?? ''} onChange={(e) => set('type', e.target.value as SourceType)} required style={inputStyle}>
              <option value="">…</option>
              {SOURCE_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></Field>
            <Field label="Status *"><select value={draft.status ?? ''} onChange={(e) => set('status', e.target.value as SourceStatus)} required style={inputStyle}>
              <option value="">…</option>
              {SOURCE_STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></Field>
            <Field label="Update frequency *"><select value={draft.update_frequency ?? ''} onChange={(e) => set('update_frequency', e.target.value as UpdateFrequency)} required style={inputStyle}>
              <option value="">…</option>
              {UPDATE_FREQUENCIES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></Field>
            <Field label="Subscription *"><select value={draft.subscription ?? ''} onChange={(e) => set('subscription', e.target.value as Subscription)} required style={inputStyle}>
              <option value="">…</option>
              {SUBSCRIPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></Field>
            <Field label="License *">
              <input value={draft.license ?? ''} onChange={(e) => set('license', e.target.value)} required placeholder="MIT / Proprietary" style={inputStyle} />
            </Field>
            <Field label="Cost (USD / yr, blank if N/A)">
              <input type="number" min={0} step="any"
                value={draft.cost_annual_usd ?? ''}
                onChange={(e) => set('cost_annual_usd', e.target.value === '' ? null : Number(e.target.value))}
                placeholder="4800"
                style={inputStyle}
              />
            </Field>
            <Field label="URL / access endpoint *">
              <input value={draft.url ?? ''} onChange={(e) => set('url', e.target.value)} required placeholder="https://… or postgres://…" style={inputStyle} />
            </Field>
            <Field label="Last reviewed *">
              <input type="date" value={draft.last_reviewed ?? ''} onChange={(e) => set('last_reviewed', e.target.value)} required style={inputStyle} />
            </Field>
            <Field label="Owner name *">
              <input
                value={draft.owner?.name ?? ''}
                onChange={(e) => onChange({ ...draft, owner: { ...(draft.owner ?? { name: '', email: '' }), name: e.target.value } })}
                required placeholder="dr the0" style={inputStyle}
              />
            </Field>
            <Field label="Owner email *">
              <input
                type="email"
                value={draft.owner?.email ?? ''}
                onChange={(e) => onChange({ ...draft, owner: { ...(draft.owner ?? { name: '', email: '' }), email: e.target.value } })}
                required placeholder="drthe0@chadwiki.com" style={inputStyle}
              />
            </Field>
            <Field label="Tools used (comma-separated)">
              <input
                value={(draft.tools_used ?? []).join(', ')}
                onChange={(e) => set('tools_used', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                placeholder="pandas, airflow" style={inputStyle}
              />
            </Field>
            <Field label="Tags (comma-separated)">
              <input
                value={(draft.tags ?? []).join(', ')}
                onChange={(e) => set('tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                placeholder="vendor, csv" style={inputStyle}
              />
            </Field>
          </div>

          <Field label="Notes">
            <textarea
              value={draft.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Rate limits, credentials location, renewal dates…"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onCancel}
              style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
            >Cancel</button>
            <button type="submit" disabled={submitting}
              style={{ padding: '6px 14px', background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer' }}
            >{submitting ? 'Saving…' : 'Save entry'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginTop: 10 }}>
      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-strong)',
  borderRadius: 4,
  padding: '6px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
};

// ── Helpers ────────────────────────────────────────────────────────────

function blankDraft(): Partial<DataSource> {
  return {
    last_reviewed: new Date().toISOString().slice(0, 10),
    owner: { name: '', email: '' },
    tags: [],
    tools_used: [],
  };
}

// Convert any Date scalar to a YYYY-MM-DD string. YAML 1.1 turns
// bare ISO dates into Date objects which React can't render. Single
// known offender today is `last_reviewed`, but defensively we cast
// any property whose runtime value is a Date.
function normalizeEntry(e: DataSource): DataSource {
  const out = { ...e };
  for (const k of Object.keys(out) as Array<keyof DataSource>) {
    const v = (out as Record<string, unknown>)[k];
    if (v instanceof Date) {
      // CTSK-303 — installed_at is a full ISO datetime (the schema
      // validates it as one); date-only truncation would flip the row
      // into a warning row. Everything else keeps the YYYY-MM-DD form.
      (out as Record<string, unknown>)[k] = k === 'installed_at' ? v.toISOString() : formatYmd(v);
    }
  }
  return out;
}

function formatYmd(d: Date): string {
  // Use UTC components so dates near midnight don't drift across
  // tz boundaries. The original YAML had no time-of-day, so any
  // timezone interpretation is arbitrary — UTC is the consistent
  // round-trip.
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeDraft(d: Partial<DataSource>): DataSource {
  // Coerce / strip empty fields, ensure required fields present.
  const out: DataSource = {
    id: (d.id ?? '').trim(),
    name: (d.name ?? '').trim(),
    type: (d.type ?? 'other') as SourceType,
    status: (d.status ?? 'evaluating') as SourceStatus,
    description: (d.description ?? '').trim(),
    url: (d.url ?? '').trim(),
    owner: { name: d.owner?.name?.trim() ?? '', email: d.owner?.email?.trim() ?? '' },
    update_frequency: (d.update_frequency ?? 'unknown') as UpdateFrequency,
    subscription: (d.subscription ?? 'internal') as Subscription,
    license: (d.license ?? '').trim(),
    last_reviewed: d.last_reviewed ?? new Date().toISOString().slice(0, 10),
  };
  if (d.cost_annual_usd != null && !Number.isNaN(d.cost_annual_usd)) out.cost_annual_usd = d.cost_annual_usd;
  if (d.tools_used && d.tools_used.length > 0) out.tools_used = d.tools_used;
  if (d.tags && d.tags.length > 0) out.tags = d.tags;
  if (d.notes && d.notes.trim()) out.notes = d.notes.trim();
  return out;
}

function pct(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((part / total) * 100);
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}
